/**
 * NCR Detail — Section 2: Order Information.
 *
 * Extracted verbatim from NCRDetailTab for the 500-line file-size rule.
 */

import { ExternalLink } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { NonConformanceReport, UserProfile, Warehouse } from '../../../lib/api';
import { NCREditFormData } from '../types';
import { Section } from '../Section';
import { DetailFieldErrors, errorProps, FieldError } from './fieldErrors';
import { parseQty, QTY_INPUT_PROPS, qtyText } from './quantity';

interface OrderInformationSectionProps {
  ncr: NonConformanceReport;
  formData: NCREditFormData;
  canEdit: boolean;
  customerUsers: UserProfile[];
  /** Show the delivery Warehouse field. Caller gates on the schedule-lines
   *  tenant flag (PlatformSettings.dashboard_tabs.scheduled). */
  showWarehouse: boolean;
  /** CR-7347: warehouses this user may pick. Empty while they load, or when
   *  the tenant has none — the field then falls back to read-only text. */
  warehouses: Warehouse[];
  /** CR-8706: fields the last Save was refused on. */
  fieldErrors?: DetailFieldErrors;
  onUpdateFormData: (field: string, value: unknown) => void;
}

export function OrderInformationSection({
  ncr,
  formData,
  canEdit,
  customerUsers,
  showWarehouse,
  warehouses,
  fieldErrors = {},
  onUpdateFormData,
}: OrderInformationSectionProps) {
  const err = (field: string, base = '') => errorProps(fieldErrors, field, base);
  const fieldError = (field: string) => <FieldError errors={fieldErrors} field={field} />;
  // The resolved warehouse ("code - name", or em-dash) — stored field first,
  // then the linked GRN / PO (CR-7347).
  const warehouseDisplay =
    [ncr.delivery_warehouse_code, ncr.delivery_warehouse_name].filter(Boolean).join(' - ') || '—';
  // Editable once the options are in. An NCR whose warehouse was only ever
  // derived has no stored id yet, so the select starts blank while the text
  // below it still shows where the order says it was received.
  const warehouseEditable = canEdit && warehouses.length > 0;
  const selectedWarehouse = (formData.warehouse as number | null | undefined) ?? null;
  const derivedOnly = selectedWarehouse === null && warehouseDisplay !== '—';

  return (
    <Section title="Order Information">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>GRN No.</Label>
          <Input
            value={formData.grn_number || ''}
            onChange={(e) => onUpdateFormData('grn_number', e.target.value)}
            disabled={!canEdit}
            {...err('grn_number')}
          />
          {fieldError('grn_number')}
          {/* CR-1154: back-link to the GRN in the deliveries view. */}
          {formData.grn_number && (
            <a
              href={`/purchasing?tab=delivery&search=${encodeURIComponent(String(formData.grn_number))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              View GRN <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div>
          <Label>PO No.</Label>
          <Input
            value={formData.po_number || ''}
            onChange={(e) => onUpdateFormData('po_number', e.target.value)}
            disabled={!canEdit}
            {...err('po_number')}
          />
          {fieldError('po_number')}
        </div>
        <div>
          <Label>PO Line No.</Label>
          {/* CR-5537: render po_line_number (po_line.line_number), not the FK id. */}
          <Input value={formData.po_line_number != null ? String(formData.po_line_number) : ''} disabled />
          {/* CR-1154: back-link to the PO line item detail. */}
          {ncr.po_line != null && (
            <a
              href={`/purchasing?tab=po_lines&line=${ncr.po_line}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              View PO line <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
      {/* Delivery warehouse from the linked GRN (schedule-line tenants). */}
      {showWarehouse && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="ncr-warehouse">Warehouse</Label>
            {warehouseEditable ? (
              <>
                <select
                  id="ncr-warehouse"
                  value={selectedWarehouse ?? ''}
                  onChange={(e) => onUpdateFormData('warehouse', e.target.value ? parseInt(e.target.value) : null)}
                  {...err('warehouse', 'w-full h-10 px-3 rounded-md border border-input bg-background text-sm')}
                >
                  <option value="">Not set</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code ? `${w.code} - ${w.name}` : w.name}
                    </option>
                  ))}
                </select>
                {derivedOnly && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    From the linked order: {warehouseDisplay}
                  </p>
                )}
              </>
            ) : (
              <Input id="ncr-warehouse" value={warehouseDisplay} disabled />
            )}
            {fieldError('warehouse')}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Part No.</Label>
          <Input
            value={formData.part_number || ''}
            onChange={(e) => onUpdateFormData('part_number', e.target.value)}
            disabled={!canEdit}
            {...err('part_number')}
          />
          {fieldError('part_number')}
        </div>
        <div className="md:col-span-2">
          <Label>Part Description</Label>
          <Input
            value={formData.description || ''}
            onChange={(e) => onUpdateFormData('description', e.target.value)}
            disabled={!canEdit}
            {...err('description')}
          />
          {fieldError('description')}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Date Code / Batch No.</Label>
          <Input
            value={formData.batch_number || ''}
            onChange={(e) => onUpdateFormData('batch_number', e.target.value)}
            disabled={!canEdit}
            {...err('batch_number')}
          />
          {fieldError('batch_number')}
        </div>
        <div>
          <Label>Works Order No.</Label>
          <Input
            value={(formData['works_order'] as string) || ''}
            onChange={(e) => onUpdateFormData('works_order', e.target.value)}
            disabled={!canEdit}
            {...err('works_order')}
          />
          {fieldError('works_order')}
        </div>
        <div>
          <Label>Buyer</Label>
          <select
            value={formData.buyer_name || ''}
            onChange={(e) => onUpdateFormData('buyer_name', e.target.value)}
            disabled={!canEdit}
            {...err('buyer_name', 'w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:cursor-not-allowed')}
          >
            <option value="">Select Buyer...</option>
            {customerUsers.map((u) => (
              <option key={u.id} value={`${u.user.first_name} ${u.user.last_name}`.trim()}>
                {`${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email}
              </option>
            ))}
          </select>
          {fieldError('buyer_name')}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Batch Qty</Label>
          <Input
            type="number"
            {...QTY_INPUT_PROPS}
            value={qtyText(formData.batch_qty)}
            onChange={(e) => onUpdateFormData('batch_qty', parseQty(e.target.value))}
            disabled={!canEdit}
            {...err('batch_qty')}
          />
          {fieldError('batch_qty')}
        </div>
        <div>
          <Label>Unit Price</Label>
          <Input
            value={formData.unit_price || ''}
            onChange={(e) => onUpdateFormData('unit_price', e.target.value)}
            disabled={!canEdit}
            {...err('unit_price')}
          />
          {fieldError('unit_price')}
        </div>
      </div>
    </Section>
  );
}
